import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { 
  Loader2, Package, Truck, CheckCircle, Clock, 
  Search, Download, Eye, ExternalLink, ArrowLeft, X, XCircle
} from "lucide-react";
import { generateInvoice } from "@/utils/invoiceGenerator";
import { toast } from "sonner";
import { InvoiceViewer } from "@/components/InvoiceViewer";

interface OrderItem {
  id: string;
  product_name: string;
  product_price: number;
  quantity: number;
  selected_size?: string;
  selected_pot_color?: string;
  selected_pot_type?: string;
  product_image_url?: string;
  shipment_id?: string;
}

interface Shipment {
  id: string;
  order_id: string;
  order_item_id: string | null;
  shiprocket_order_id: string | null;
  shiprocket_shipment_id: string | null;
  tracking_number: string | null;
  courier_name: string | null;
  tracking_url: string | null;
  shipment_status: string | null;
  shipped_date: string | null;
  estimated_delivery_date: string | null;
}

interface Order {
  id: string;
  order_number?: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  shipping_address: any;
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  currency: string;
  payment_status: string;
  order_status: string;
  created_at: string;
  order_items: OrderItem[];
  tracking_number?: string;
  courier_name?: string;
  tracking_url?: string;
  shipped_date?: string;
  estimated_delivery_date?: string;
  refund_status?: string | null;
  refund_amount?: number | null;
  shipments?: Shipment[];
}

interface OrderLookupSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const cancellationReasons = [
  { value: 'ordered_by_mistake', label: 'Ordered by mistake' },
  { value: 'found_better_price', label: 'Found a better price elsewhere' },
  { value: 'delivery_time_long', label: 'Delivery time is too long' },
  { value: 'change_in_plans', label: 'Plans have changed / no longer need the item' },
  { value: 'payment_issue', label: 'Issue with payment or billing' },
  { value: 'other', label: 'Other' },
];

export const OrderLookupSheet = ({ open, onOpenChange }: OrderLookupSheetProps) => {
  const navigate = useNavigate();
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invoiceViewerOpen, setInvoiceViewerOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [cancelReasonOption, setCancelReasonOption] = useState("");
  const [cancelReasonOther, setCancelReasonOther] = useState("");

  // Reset all state when sheet is closed
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset all state when closing
      setOrderId("");
      setEmail("");
      setOrder(null);
      setError(null);
      setLoading(false);
      setCancelReasonOption("");
      setCancelReasonOther("");
    }
    onOpenChange(isOpen);
  };

  const handleSearch = async () => {
    const trimmedId = orderId.trim();
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedId) {
      toast.error("Please enter an order ID");
      return;
    }
    
    if (!trimmedEmail) {
      toast.error("Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError(null);
    setOrder(null);

    try {
      // Use the secure database function that requires email verification
      const { data: orderData, error: rpcError } = await supabase
        .rpc('find_order_by_id_with_verification', { 
          partial_id: trimmedId,
          verification_email: trimmedEmail 
        });

      if (rpcError) {
        console.error('Error fetching order:', rpcError);
        setError("Failed to find order. Please try again.");
        return;
      }

      if (!orderData || orderData.length === 0) {
        setError("Order not found. Please check the order ID and email address match your order confirmation.");
        return;
      }

      // Get the first matching order
      const foundOrder = orderData[0];

      // Now fetch order items separately
      const { data: orderItems, error: itemsError } = await supabase
        .from('order_items')
        .select('*')
        .eq('order_id', foundOrder.id);

      if (itemsError) {
        console.error('Error fetching order items:', itemsError);
      }

      // Fetch shipments for this order
      const { data: shipmentsData, error: shipmentsError } = await supabase
        .from('order_shipments')
        .select('*')
        .eq('order_id', foundOrder.id);

      if (shipmentsError) {
        console.error('Error fetching shipments:', shipmentsError);
      }

      // Combine order with items and shipments
      setOrder({
        ...foundOrder,
        order_items: orderItems || [],
        shipments: shipmentsData || []
      });
    } catch (err) {
      console.error('Error:', err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = () => {
    if (!order) return;
    try {
      generateInvoice(order);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    }
  };

  const handleViewInvoice = () => {
    setInvoiceViewerOpen(true);
  };

  const handleTrack = () => {
    if (!order) return;
    onOpenChange(false);
    navigate(`/track/${order.id}`);
  };

  const handleReset = () => {
    setOrder(null);
    setError(null);
    setOrderId("");
    setEmail("");
  };

  const canCancelOrder = (status: string) => {
    return status === 'order_placed' || status === 'processing';
  };

  const handleCancelOrder = async () => {
    if (!order || !canCancelOrder(order.order_status)) {
      toast.error('This order can no longer be cancelled.');
      return;
    }

    const selected = cancellationReasons.find((r) => r.value === cancelReasonOption);
    const baseLabel = selected?.label ?? 'Other';
    const finalReason = cancelReasonOption === 'other'
      ? `Other: ${cancelReasonOther.trim()}`
      : baseLabel;

    if (!finalReason.trim()) {
      toast.error('Please select a reason for cancelling your order.');
      return;
    }

    try {
      setCancellingOrder(true);

      const { data, error: cancelError } = await supabase.functions.invoke('cancel-shiprocket-order', {
        body: { orderId: order.id, reason: finalReason.slice(0, 500) },
      });

      if (cancelError) {
        console.error('Error cancelling order:', cancelError);
        toast.error(cancelError.message || 'Failed to cancel order. Please try again.');
        return;
      }

      if (!data?.success) {
        const code = (data as any)?.code;
        if (code === 'ALREADY_SHIPPED') {
          toast.error('Order already shipped, cannot cancel.');
        } else if (code === 'ALREADY_CANCELLED') {
          toast.error('Order already cancelled.');
        } else {
          const message = (data as any)?.message || 'Failed to cancel order. Please try again.';
          toast.error(message);
        }
        return;
      }

      // Update local order state
      setOrder({
        ...order,
        order_status: 'cancelled',
        refund_status: (order.payment_status === 'completed' || order.payment_status === 'success')
          ? 'pending'
          : order.refund_status ?? null,
        refund_amount: (order.payment_status === 'completed' || order.payment_status === 'success')
          ? order.total_amount
          : order.refund_amount ?? null,
      });

      toast.success(
        'Your order has been cancelled. If you paid online, your refund will be processed back to your original payment method within 5–7 business days.',
      );
    } catch (err) {
      console.error('Unexpected error cancelling order:', err);
      toast.error('Failed to cancel order. Please try again.');
    } finally {
      setCancellingOrder(false);
      setCancelDialogOpen(false);
      setCancelReasonOption('');
      setCancelReasonOther('');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'order_placed': return <Clock className="h-4 w-4" />;
      case 'processing': return <Package className="h-4 w-4" />;
      case 'shipped': return <Truck className="h-4 w-4" />;
      case 'out_for_delivery': return <Truck className="h-4 w-4" />;
      case 'delivered': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'order_placed': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'out_for_delivery': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="left" className="w-full sm:max-w-md p-0 overflow-y-auto">
          <SheetHeader className="px-6 py-5 border-b border-border">
            <div className="flex items-center justify-between">
              {order ? (
                <Button variant="ghost" size="sm" onClick={handleReset} className="-ml-2">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              ) : (
                <SheetTitle>Track Your Order</SheetTitle>
              )}
              <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="h-9 w-9 -mr-2">
                <X className="h-5 w-5" />
              </Button>
            </div>
          </SheetHeader>

          <div className="p-6">
            {!order ? (
              // Search Form
              <div className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Enter your order ID and email address to view order details, track shipment, and download invoice.
                </p>
                
                <div className="space-y-3">
                  <Input
                    placeholder="Enter Order ID (e.g., ORD-001002 or a1b2c3d4)"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    className="h-12"
                  />
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="h-12"
                  />
                  <Button 
                    onClick={handleSearch} 
                    disabled={loading || !orderId.trim() || !email.trim()}
                    className="w-full h-12"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Search className="h-4 w-4 mr-2" />
                    )}
                    Find Order
                  </Button>
                </div>

                {error && (
                  <div className="p-4 bg-destructive/10 text-destructive rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="text-xs text-muted-foreground space-y-2">
                  <p className="font-medium">How to find your order:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Check your order confirmation email</li>
                    <li>Use the Order ID and email from that confirmation</li>
                  </ul>
                </div>
              </div>
            ) : (
              // Order Details
              <div className="space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">Order #{order.order_number || order.id.slice(-8)}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {format(new Date(order.created_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                      <Badge className={getStatusColor(order.order_status)} variant="secondary">
                        <span className="flex items-center gap-1">
                          {getStatusIcon(order.order_status)}
                          {order.order_status.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">

                    {/* Order Items */}
                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-3 text-sm">Items</h4>
                      <div className="space-y-2">
                        {order.order_items.map((item) => (
                          <div key={item.id} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                            {item.product_image_url && item.product_image_url !== '/placeholder.svg' ? (
                              <img 
                                src={item.product_image_url} 
                                alt={item.product_name}
                                className="w-10 h-10 object-cover rounded"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                                <Package className="h-5 w-5 text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{item.product_name}</p>
                              <p className="text-xs text-muted-foreground">
                                Qty: {item.quantity} × ₹{item.product_price}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order Total */}
                    <div className="pt-4 border-t space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>₹{order.subtotal}</span>
                      </div>
                      {order.discount_amount > 0 && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Discount</span>
                          <span>-₹{order.discount_amount}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Shipping</span>
                        <span>{order.shipping_cost > 0 ? `₹${order.shipping_cost}` : 'Free'}</span>
                      </div>
                      <div className="flex justify-between font-semibold pt-2 border-t">
                        <span>Total</span>
                        <span>₹{order.total_amount}</span>
                      </div>
                    </div>

                    {/* Refund Info */}
                    {order.refund_status && (
                      <div className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
                        <p className="font-medium">Refund {order.refund_status}</p>
                        {order.refund_amount && (
                          <p className="text-xs mt-1">Amount: ₹{order.refund_amount}</p>
                        )}
                      </div>
                    )}

                    {/* Per-shipment Shipping Status - iterate through shipments to handle qty > 1 */}
                    {order.shipments && order.shipments.length > 0 && (
                      <div className="pt-4 border-t space-y-3">
                        <h4 className="font-medium text-sm">Shipping Status ({order.shipments.length} packages)</h4>
                        {order.shipments.map((shipment, index) => {
                            // Find the order item associated with this shipment
                            const item = order.order_items.find(i => i.shipment_id === shipment.id || i.id === shipment.order_item_id);
                            const hasTracking = !!shipment.tracking_number;
                            
                            return (
                              <div key={shipment.id} className={`p-3 rounded-lg space-y-2 text-sm ${hasTracking ? 'bg-muted/50' : 'bg-amber-50/50 border border-amber-200/50'}`}>
                                {/* Product details */}
                                {item ? (
                                  <div className="flex items-center gap-2 pb-2 border-b border-border/50 mb-2">
                                    {item.product_image_url && (
                                      <img 
                                        src={item.product_image_url} 
                                        alt={item.product_name}
                                        className="w-10 h-10 object-cover rounded"
                                      />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm truncate">{item.product_name}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {item.selected_size && `${item.selected_size}`}
                                        {item.selected_pot_type && ` • ${item.selected_pot_type}`}
                                        {item.selected_pot_color && ` • ${item.selected_pot_color}`}
                                        {` • Qty: 1`}
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="font-medium text-xs text-muted-foreground pb-2 border-b border-border/50 mb-2">
                                    Package {index + 1}
                                  </p>
                                )}

                                {hasTracking ? (
                                  <>
                                    {shipment.courier_name && (
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Courier</span>
                                        <span className="font-medium">{shipment.courier_name}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Tracking #</span>
                                      <span className="font-mono text-xs">{shipment.tracking_number}</span>
                                    </div>
                                    {shipment.shipment_status && (
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Status</span>
                                        <Badge variant="secondary" className="text-xs">
                                          {shipment.shipment_status.replace(/_/g, ' ').toUpperCase()}
                                        </Badge>
                                      </div>
                                    )}
                                    {shipment.estimated_delivery_date && (
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Expected</span>
                                        <span>{format(new Date(shipment.estimated_delivery_date), 'MMM dd, yyyy')}</span>
                                      </div>
                                    )}
                                    {shipment.tracking_url && (
                                      <Button variant="outline" size="sm" className="w-full mt-2" asChild>
                                        <a href={shipment.tracking_url} target="_blank" rel="noopener noreferrer">
                                          <ExternalLink className="h-3 w-3 mr-2" />
                                          Track on {shipment.courier_name || 'Courier'}
                                        </a>
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <div className="flex items-center gap-2 text-amber-700">
                                    <Clock className="h-4 w-4" />
                                    <span className="text-sm">Preparing for shipment</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}

                    {/* Legacy Tracking Info (fallback for orders without shipments) */}
                    {(!order.shipments || order.shipments.length === 0) && order.tracking_number && (
                      <div className="p-3 bg-muted/50 rounded-lg space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Courier</span>
                          <span className="font-medium">{order.courier_name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tracking #</span>
                          <span className="font-mono text-xs">{order.tracking_number}</span>
                        </div>
                        {order.estimated_delivery_date && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Expected</span>
                            <span>{format(new Date(order.estimated_delivery_date), 'MMM dd')}</span>
                          </div>
                        )}
                        {order.tracking_url && (
                          <Button variant="outline" size="sm" className="w-full mt-2" asChild>
                            <a href={order.tracking_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-3 w-3 mr-2" />
                              Track on {order.courier_name || 'Courier'}
                            </a>
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {(order.payment_status === 'completed' || order.payment_status === 'success') && (
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={handleViewInvoice} className="flex-1">
                        <Eye className="h-4 w-4 mr-2" />
                        View Invoice
                      </Button>
                      <Button variant="outline" onClick={handleDownloadInvoice} className="flex-1">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  )}

                  {/* Cancel Order Button - only for order_placed or processing status */}
                  {canCancelOrder(order.order_status) && (
                    <Button 
                      variant="destructive" 
                      className="w-full"
                      onClick={() => setCancelDialogOpen(true)}
                      disabled={cancellingOrder}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel Order
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Invoice Viewer Modal */}
      <InvoiceViewer 
        order={order}
        open={invoiceViewerOpen}
        onClose={() => setInvoiceViewerOpen(false)}
      />

      {/* Cancel Order Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setCancelDialogOpen(false);
          setCancelReasonOption("");
          setCancelReasonOther("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this order?</DialogTitle>
            <DialogDescription>
              Please tell us why you're cancelling this order. If your payment was made online, we'll automatically
              start a refund to your original payment method. Refunds typically take 5–7 business days to appear,
              depending on your bank or card issuer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm font-medium">Reason for cancellation</p>
            <Select
              value={cancelReasonOption}
              onValueChange={(value) => {
                setCancelReasonOption(value);
                if (value !== 'other') {
                  setCancelReasonOther('');
                }
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent className="z-50">
                {cancellationReasons.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {cancelReasonOption === 'other' && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Please tell us a bit more</p>
                <Textarea
                  value={cancelReasonOther}
                  onChange={(e) => setCancelReasonOther(e.target.value)}
                  placeholder="E.g. specific issue with this order, delivery concerns, etc."
                  rows={4}
                />
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCancelDialogOpen(false);
                setCancelReasonOption("");
                setCancelReasonOther("");
              }}
            >
              Keep order
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={
                !cancelReasonOption ||
                (cancelReasonOption === 'other' && !cancelReasonOther.trim()) ||
                cancellingOrder
              }
              onClick={handleCancelOrder}
            >
              {cancellingOrder && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
